import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BackButton } from "../../../components/buttons/backButton";
import { Button } from "../../../components/buttons/button";
import { InfoCard } from "../../../components/cards/infoCard";
import { ProfileIcon } from "../../../components/cards/profilePic";
import { CalendarOptionsModal } from "../../../components/modal/app_modals/calendarOptionsModal";
import { ScrollToTop } from "../../../components/scrollToTop";
import { TicketBody } from "../../../components/ticket";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../customeHooks/useModalControl";
import { AuthProfile } from "../../../redux/features/auth/auth.types";
import { BuyTicketsResponse } from "../../../redux/features/event/event.types";
import { useAppSelector } from "../../../redux/hooks";
import {
  APP_PORT,
  APP_SCHEME,
  APP_SUBDOMAIN,
  guestEventsLSKey,
  guestProfileLSKey,
} from "../../../utils/constants";
import {
  formatDateTime,
  formatToNaira,
  handleDownloadPdf,
} from "../../../utils/helpers";
import { LSGuestDetails, TicketPurchase } from "../../../utils/types";

export const PurchaseSuccess = ({
  purchaseResponse,
  ticketsPurchase,
}: {
  purchaseResponse: BuyTicketsResponse;
  ticketsPurchase: TicketPurchase[];
}) => {
  const [showTicket, setShowTicket] = useState(false);

  const navigate = useNavigate();

  const { isSignedIn } = useAppSelector((state) => state.auth);

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const ticketRef = useRef<HTMLDivElement>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex w-full justify-center" ref={containerRef}>
      <ScrollToTop ref={containerRef} />
      <div className="mx-auto flex w-full flex-col items-center gap-3">
        {showTicket ? (
          <>
            <div className="w-full max-w-[460px]">
              <BackButton
                text="Back"
                onClick={() => {
                  if (isSignedIn) {
                    window.open(
                      `${APP_SCHEME}//${APP_SUBDOMAIN}:${APP_PORT}/home`,
                      "_self",
                    );
                  } else {
                    navigate(-1);
                    // navigate("/home/guest");
                    localStorage.setItem(
                      guestEventsLSKey,
                      JSON.stringify([
                        {
                          guest: purchaseResponse.data.guest.guestResponse,
                          purchase: ticketsPurchase,
                        },
                      ] as LSGuestDetails),
                    );

                    localStorage.setItem(
                      guestProfileLSKey,
                      JSON.stringify({
                        firstName:
                          purchaseResponse.data.guest.guestResponse.firstName,
                        lastName:
                          purchaseResponse.data.guest.guestResponse.lastName,
                        email: purchaseResponse.data.guest.guestResponse.email,
                      } as Partial<AuthProfile>),
                    );
                  }
                }}
              />
            </div>

            <TicketBody
              ref={ticketRef}
              downloadView={false}
              eventName={
                purchaseResponse?.data?.guest?.guestResponse?.eventId?.name ||
                ""
              }
              startDate={
                purchaseResponse?.data?.guest?.guestResponse?.eventId
                  ?.startDate || ""
              }
              endDate={
                purchaseResponse?.data?.guest?.guestResponse?.eventId
                  ?.endDate || ""
              }
              startTime={
                purchaseResponse?.data?.guest?.guestResponse?.eventId
                  ?.startTime || ""
              }
              location={
                purchaseResponse?.data?.guest?.guestResponse?.eventId
                  ?.location || ""
              }
              qrCode={
                purchaseResponse?.data?.guest?.guestResponse?.qrCode || ""
              }
              firstName={
                purchaseResponse?.data?.guest?.guestResponse?.firstName || ""
              }
              lastName={
                purchaseResponse?.data?.guest?.guestResponse?.lastName || ""
              }
              email={purchaseResponse?.data?.guest?.guestResponse?.email || ""}
              ticketsPurchase={ticketsPurchase || []}
              payment={purchaseResponse?.data?.guest?.guestResponse?.payment}
            />

            <Button
              type="button"
              className="!max-w-[460px] !rounded-2xl"
              onClick={async () => {
                await handleDownloadPdf(
                  ticketRef,
                  `${purchaseResponse?.data?.guest?.guestResponse?.eventId?.name}_ticket`,
                );
              }}
              text="Download"
              icon="download"
              variation="white"
            />
          </>
        ) : (
          <>
            <BgWrapper className="flex w-full max-w-[460px] flex-col items-center justify-center gap-4 !py-10">
              <img
                alt="calendar"
                src="/assets/images/check-glass.png"
                className="h-[120px] w-[120px]"
              />

              <div className="mx-auto w-full max-w-[320px] text-center">
                <p className="text-32 font-fraunces font-medium text-primary">
                  Your ticket purchase was successful!
                </p>
                <p className="mt-2 text-grey-600">
                  We’ve received your payment. Your ticket has been sent to{" "}
                  {purchaseResponse?.data?.guest?.guestResponse?.email}.
                </p>
              </div>
            </BgWrapper>
            <BgWrapper className="max-w-[460px] !p-4 text-grey-900">
              <div className="flex items-center justify-between gap-4 border-b border-grey-100 pb-4">
                <div className="flex flex-col">
                  <p className="text-base font-bold">
                    {purchaseResponse?.data?.guest?.guestResponse?.firstName}{" "}
                    {purchaseResponse?.data?.guest?.guestResponse?.lastName}
                  </p>
                  <p className="text-sm">
                    {purchaseResponse?.data?.guest?.guestResponse?.email}
                  </p>
                </div>

                <ProfileIcon dimension={40} className="!p-[10px]" />
              </div>

              {ticketsPurchase.map((tp, idx) => {
                return (
                  <div
                    key={`${idx}-${tp.ticket._id}`}
                    className="flex flex-col gap-[2px] border-b border-grey-100 py-4 text-sm last:border-b-0 last:pb-0"
                  >
                    <p>
                      {tp.ticket.name} x {tp.quantity}
                    </p>
                    <p className="font-bold">
                      {tp.ticket.ticketType === "free"
                        ? "Free"
                        : formatToNaira(tp.quantity * tp.ticket.price)}
                    </p>
                  </div>
                );
              })}
            </BgWrapper>
            <div className="grid w-full max-w-[460px] grid-cols-1 gap-3 md:grid-cols-2">
              <Button
                type="button"
                className="!rounded-2xl"
                onClick={() => {
                  handleModalOpen("calendarOptionsModal");
                }}
                text="Add to calendar"
                icon="calendar-plus"
                variation="white"
              />

              <Button
                type="button"
                className="!rounded-2xl"
                onClick={() => {
                  setShowTicket(true);
                }}
                text="View Ticket"
                icon="ticket"
                variation="white"
              />
            </div>
            {!isSignedIn && (
              <InfoCard
                title="Buy Tickets Faster ⚡ "
                text="Sign up to save your details to buy tickets in one-click."
                className="max-w-[460px] !bg-light-blue"
                rightElement={
                  <Button
                    type="button"
                    text="Sign Up"
                    onClick={() => {
                      navigate(
                        `/signup/?email=${encodeURIComponent(purchaseResponse?.data?.guest?.guestResponse?.email)}&firstName=${encodeURIComponent(purchaseResponse?.data?.guest?.guestResponse?.firstName)}&lastName=${encodeURIComponent(purchaseResponse?.data?.guest?.guestResponse?.lastName)}`,
                      );
                    }}
                    className="!max-w-fit"
                  />
                }
              />
            )}{" "}
          </>
        )}
      </div>

      <CalendarOptionsModal
        open={modalOpen && modal === "calendarOptionsModal"}
        handleClose={() => {
          handleModalClose();
        }}
        event={{
          title:
            purchaseResponse?.data?.guest?.guestResponse?.eventId?.name ?? "",
          description:
            purchaseResponse?.data?.guest?.guestResponse?.eventId
              ?.description ?? "",
          location:
            purchaseResponse?.data?.guest?.guestResponse?.eventId?.location ??
            "",
          start: formatDateTime(
            purchaseResponse?.data?.guest?.guestResponse?.eventId?.startDate ??
              "",
            purchaseResponse?.data?.guest?.guestResponse?.eventId?.startTime ??
              "",
          ),
          end: formatDateTime(
            purchaseResponse?.data?.guest?.guestResponse?.eventId?.endDate ??
              "",
            purchaseResponse?.data?.guest?.guestResponse?.eventId?.endTime ??
              "",
          ),
        }}
      />
    </div>
  );
};
